/*!
 * Yoda.js v0.2.1
 * (c) 2019-present By Kuaishou
 * Released under the ISC License.
 */
'use strict';

var _readyResolve;

var _readyPromise = new Promise(function (resolve) {
  _readyResolve = resolve;
});

function ready(callback) {
  if (callback) {
    _readyPromise.then(callback);
  }

  return _readyPromise;
}

/**
 * 注册 api
 * @param {object} obj
 *              namespace      命名空间
 *              name    api名
 *              before  format 请求参数
 *              after   format 返回值
 */
function register(config) {
  var _this = this;

  if (!(config && config.namespace && config.name)) {
    throw new Error('register 参数： {namespace, name}');
  }

  var namespace = this[config.namespace];

  if (!namespace) {
    namespace = this[config.namespace] = {};
  } // 可能是以下几种情况： (), (params), (params, option), (params, callback), (params, option, callback)


  namespace[config.name] = function (params, option, callback) {
    params = params || {};

    if (!callback && typeof option === 'function') {
      callback = option;
      option = null;
    }

    option = option || {};

    callback = callback || function () {};

    if (config.before) {
      // before可能会报错 直接抛出异常
      params = config.before(params);
    }

    var callbackId = ++_this._callbackId;
    var returnPromise;
    var invokeCallback;

    if (config.multiCallback || option.multiCallback) {
      invokeCallback = callback;
    } else {
      returnPromise = new Promise(function (resolve) {
        return invokeCallback = resolve;
      });
      returnPromise.then(function () {
        delete _this._callbacks[callbackId];
      });
      returnPromise.then(callback);
    }

    if (config.after) {
      var originInvokeCallback = invokeCallback;

      invokeCallback = function invokeCallback(ret) {
        return originInvokeCallback(config.after(ret));
      };
    }

    _this._callbacks[callbackId] = invokeCallback;

    try {
      _this.invoke({
        namespace: config.namespace,
        name: config.name,
        params: params,
        callbackId: callbackId
      });
    } catch (e) {
      invokeCallback({
        // 桥调用异常
        result: 125014,
        message: e.message
      });
    }

    return returnPromise;
  };
}

/**
 *
 * @param {string} namespace
 * @param {string} name 接口名
 * @param {object} params 参数
 * @param {function} callback 回调函数
 */
function invoke(_ref) {
  var namespace = _ref.namespace,
      name = _ref.name,
      params = _ref.params,
      callbackId = _ref.callbackId;

  // callbackId 约定为string
  window.__yodaBridge__.invoke(namespace, name, JSON.stringify(params), '' + callbackId);
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var baseApis = [{
  namespace: 'system',
  name: 'getDeviceInfo'
}, {
  namespace: 'system',
  name: 'getAppInfo'
}, {
  namespace: 'system',
  name: 'getNetworkType'
}, {
  namespace: 'webview',
  name: 'open'
}, {
  namespace: 'webview',
  name: 'getLaunchParams'
}, {
  namespace: 'webview',
  name: 'close'
}, {
  namespace: 'webview',
  name: 'backOrClose'
}, {
  namespace: 'tool',
  name: 'canIUse'
}, {
  namespace: 'tool',
  name: 'checkAppInstalled'
}, {
  namespace: 'tool',
  name: 'launchApp'
}, {
  namespace: 'ui',
  name: 'setTopBarStyle'
}, {
  namespace: 'ui',
  name: 'setTitle'
}, {
  namespace: 'ui',
  name: 'setTopBarButton'
}, {
  namespace: 'ui',
  name: 'removeTopBarButton'
}, {
  namespace: 'ui',
  name: 'setSlideBackBehavior'
}, {
  namespace: 'ui',
  name: 'setPhysicalBackButtonBehavior'
}, {
  namespace: 'ui',
  name: 'showLoading'
}, {
  namespace: 'ui',
  name: 'hideLoading'
}, {
  namespace: 'ui',
  name: 'showToast'
}, {
  namespace: 'ui',
  name: 'showDialog'
}, {
  namespace: 'event',
  name: 'addEventListener'
}, {
  namespace: 'event',
  name: 'removeEventListener'
}, {
  namespace: 'event',
  name: 'dispatchEvent'
}];
var isAndroid = navigator.userAgent.indexOf(' Android ') > -1;
var overWriteApis = {
  'tool.checkAppInstalled': {
    before: function before(params) {
      return {
        identifier: params[isAndroid ? 'android' : 'ios']
      };
    }
  }
};

function addEventListener(type, listener, callback) {
  if (listener.__yodaCallbackId__) {
    throw new Error('addEventListener 的 listener 已经被绑定过了');
  }

  this._callbackId++;
  this._callbacks[this._callbackId] = listener;
  var eventSet = this._eventListeners[type];

  if (!eventSet) {
    eventSet = this._eventListeners[type] = {};
  }

  eventSet[this._callbackId] = true;
  listener.__yodaCallbackId__ = '' + this._callbackId;
  return this.event._addEventListener({
    type: type,
    listener: listener.__yodaCallbackId__
  }, callback);
} // 不传 listener 时，需要移除该 type 下的所有 listener


function removeEventListener(type, listener, callback) {
  var _this = this;

  var listenerId;
  var eventSet = this._eventListeners[type];

  if (listener) {
    listenerId = listener.__yodaCallbackId__;

    if (!(listenerId && eventSet && eventSet[listenerId])) {
      // 已经被删除（可能是批量删除） 不需要调用客户端 直接返回成功
      var resPromise = Promise.resolve({
        result: 1
      });
      callback && resPromise.then(callback);
      return resPromise;
    }
  }

  var promise = this.event._removeEventListener({
    type: type,
    listener: listenerId
  }, callback); // bridge 成功时清除回调


  promise.then(function (ret) {
    // 约定 客户端没有找到对应的 listenerId 也返回 result: 1
    // 约定 result 不为1的时候不删
    if (ret.result === 1) {
      if (listener) {
        delete eventSet[listenerId];
        delete listener.__yodaCallbackId__;
        delete _this._callbacks[listenerId];
      } else {
        // 不传 listener 时，需要移除该 name 下的所有 listener
        if (eventSet) {
          delete _this._eventListeners[name];

          for (var key in eventSet) {
            delete _this._callbacks[key].__yodaCallbackId__;
            delete _this._callbacks[key];
          }
        }
      }
    }
  });
  return promise;
}

function initEvent(yoda) {
  if (yoda.event.addEventListener) {
    yoda.event._addEventListener = yoda.event.addEventListener;
    yoda.event.addEventListener = addEventListener.bind(yoda);
  }

  if (yoda.event.removeEventListener) {
    yoda.event._removeEventListener = yoda.event.removeEventListener;
    yoda.event.removeEventListener = removeEventListener.bind(yoda);
  }
}

function init() {
  var yoda = this;

  var already = function already() {
    yoda.register({
      namespace: 'tool',
      name: 'getApiList'
    });
    yoda.tool.getApiList({}, function (_ref) {
      var result = _ref.result,
          apiList = _ref.apiList;

      // 方法不存在时使用兜底api
      if (result !== 1) {
        apiList = baseApis;
      }

      for (var l = apiList.length; l--;) {
        var api = apiList[l]; // 某些方法需要添加 before 或者 after 等

        var overWriteApi = overWriteApis[api.namespace + '.' + api.name];

        if (overWriteApi) {
          api = _objectSpread2({}, api, {}, overWriteApi);
        }

        yoda.register(api);
      }

      initEvent(yoda); // 触发 yoda.ready

      yoda._readyResolve();
    });
  }; // 对已经注入bridge的 直接ready


  if (window.__yodaBridge__) {
    already();
  } else {
    window.__yodaBridgeReady__ = already;
  }
}

// 准备aop插件系统
function setupPlugin(yoda, config) {
  yoda._plugins = [];

  var _loop = function _loop(name) {
    var realFn = config[name];
    var surfix = name[0].toUpperCase() + name.slice(1);
    var beforeName = 'before' + surfix;
    var afterName = 'after' + surfix;

    yoda[name] = function (params) {
      var plugins = yoda._plugins;
      var length = plugins.length;
      var index = -1;

      var next = function next() {
        while (++index < length) {
          var fn = plugins[index][beforeName];

          if (typeof fn === 'function') {
            var _ret = function () {
              var called = void 0;
              fn.call(yoda, params, function () {
                if (!called) {
                  called = true;
                  next();
                }
              });
              return {
                v: void 0
              };
            }();

            if (_typeof(_ret) === "object") return _ret.v;
          }
        }

        realFn.call(yoda, params);

        for (var i = 0; i < length; i++) {
          var _fn = plugins[i][afterName];

          if (typeof _fn === 'function') {
            _fn.call(yoda, params);
          }
        }
      };

      next();
    };
  };

  for (var name in config) {
    _loop(name);
  }

  return function plugin(config) {
    // config 可以是 class 或者 对象
    if (typeof config === 'function') {
      config = new config(yoda);
    }

    yoda._plugins.push(config);
  };
}

function callbackHandler (_ref) {
  var id = _ref.id,
      params = _ref.params;
  var callback = this._callbacks[id];

  if (callback) {
    callback(params);
  } // TODO 回调不存在 上报
  // TODO result 不为1 上报

}

// 与客户端约定的接口描述
var yoda = {
  version: '0.2.1',
  isInYoda: !!window.__yodaBridge__,
  // 自增回调ID
  _callbackId: 1,

  /**
   * 存放着回调函数
   *  {
   *      1: function(){}
   *  }
   */
  _callbacks: {},

  /**
   * 存放着事件回调
   * {
   *      resume: {
   *          45: true
   *      }
   * }
   */
  _eventListeners: {},
  // ready 的 promise 对象
  _readyPromise: _readyPromise,
  // ready 的 promise 的 resolve 方法
  _readyResolve: _readyResolve,
  // ready 方法
  ready: ready
}; // 准备插件系统

yoda.plugin = setupPlugin(yoda, {
  register: register,
  invoke: invoke,
  callbackHandler: callbackHandler,
  init: init
}); // 用于接收客户端回调

window.__yodaBridgeCallback__ = function (id, params) {
  yoda.callbackHandler({
    id: id,
    params: params
  });
}; // 下一个 event loop 再初始化, 为了可以改写 init 方法


setTimeout(function () {
  return yoda.init();
});

module.exports = yoda;
